import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Search, Check, RotateCcw, CircleDot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminListProducts, adminBulkPrice, adminPriceStatus, apiErr } from "@/lib/api";

const istDay = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return new Date(d.getTime() + 3 * 3600000).toISOString().slice(0, 10);
  } catch {
    return null;
  }
};
const todayIst = () => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 10);
const round5 = (n) => Math.round(n / 5) * 5;

export default function QuickPriceManager() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [roundOn, setRoundOn] = useState(true);
  const [saving, setSaving] = useState(false);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["admin", "products"],
    queryFn: adminListProducts,
  });

  const today = todayIst();
  const bySlug = useMemo(() => Object.fromEntries(products.map((p) => [p.slug, p])), [products]);
  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(),
    [products]
  );

  const priceVal = (slug) =>
    edits[slug]?.price !== undefined ? edits[slug].price : String(bySlug[slug]?.price ?? "");
  const statusVal = (slug) =>
    edits[slug]?.status !== undefined ? edits[slug].status : bySlug[slug]?.status || "available";

  const isPriceDirty = (slug) =>
    edits[slug]?.price !== undefined && Number(edits[slug].price) !== Number(bySlug[slug]?.price);
  const isStatusDirty = (slug) =>
    edits[slug]?.status !== undefined && edits[slug].status !== (bySlug[slug]?.status || "available");
  const isDirty = (slug) => isPriceDirty(slug) || isStatusDirty(slug);

  const dirtyList = products.filter((p) => isDirty(p.slug));

  const setPrice = (slug, v) =>
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], price: v.replace(",", ".") } }));
  const blurPrice = (slug) => {
    const raw = edits[slug]?.price;
    if (raw === undefined || raw === "") return;
    let n = Number(raw);
    if (!Number.isFinite(n)) return;
    if (roundOn) n = round5(n);
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], price: String(n) } }));
  };
  const setStatus = (slug, on) =>
    setEdits((e) => ({ ...e, [slug]: { ...e[slug], status: on ? "available" : "passive" } }));

  const reset = () => setEdits({});

  const save = async () => {
    const items = dirtyList.map((p) => {
      const it = { slug: p.slug };
      if (isPriceDirty(p.slug)) it.price = Number(edits[p.slug].price);
      if (isStatusDirty(p.slug)) it.status = edits[p.slug].status;
      return it;
    });
    setSaving(true);
    try {
      const res = await adminBulkPrice(items);
      toast.success(`${res.updated} ürün güncellendi`);
      setEdits({});
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
      qc.invalidateQueries({ queryKey: ["admin", "price-status"] });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const filtered = products.filter((p) => {
    if (cat !== "all" && p.category !== cat) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const updatedToday = products.filter((p) => istDay(p.price_updated_at) === today).length;

  return (
    <div data-testid="quick-price-manager" className="pb-24">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Hızlı Fiyat Güncelleme</h3>
          <p className="text-sm text-gray-500">
            {products.length} ürün · bugün güncellenen:{" "}
            <span className={updatedToday > 0 ? "text-emerald-600 font-medium" : "text-amber-600 font-medium"}>
              {updatedToday}
            </span>
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600" data-testid="round-toggle-label">
          <Switch checked={roundOn} onCheckedChange={setRoundOn} data-testid="round-toggle" />
          Fiyatları 5₺'ye yuvarla
        </label>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="quick-price-search"
            placeholder="Ürün ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[180px]" data-testid="quick-price-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const fresh = istDay(p.price_updated_at) === today;
            const dirty = isDirty(p.slug);
            const passive = statusVal(p.slug) === "passive";
            return (
              <div
                key={p.slug}
                data-testid={`quick-price-row-${p.slug}`}
                className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 transition-colors ${
                  dirty
                    ? "border-[#C57B43] bg-[#C57B43]/5"
                    : fresh
                    ? "border-gray-200 bg-white"
                    : "border-amber-200 bg-amber-50/40"
                } ${passive ? "opacity-60" : ""}`}
              >
                <img src={p.image} alt="" className="h-11 w-11 rounded object-cover bg-gray-100 shrink-0" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-800">{p.name}</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5">{p.category}</span>
                    <CircleDot className={`h-3 w-3 ${fresh ? "text-emerald-500" : "text-amber-500"}`} />
                    <span>{fresh ? "bugün güncel" : p.price_updated_at ? `son: ${istDay(p.price_updated_at)}` : "hiç güncellenmedi"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="relative">
                    <Input
                      data-testid={`quick-price-input-${p.slug}`}
                      type="number"
                      inputMode="decimal"
                      step="1"
                      value={priceVal(p.slug)}
                      onChange={(e) => setPrice(p.slug, e.target.value)}
                      onBlur={() => blurPrice(p.slug)}
                      className="w-24 pr-7 text-right font-mono"
                    />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">₺</span>
                  </div>
                  <span className="w-8 text-xs text-gray-400">/{p.unit || "kg"}</span>
                  <label className="flex items-center gap-1.5 text-[11px]">
                    <Switch
                      data-testid={`quick-price-status-${p.slug}`}
                      checked={!passive}
                      onCheckedChange={(on) => setStatus(p.slug, on)}
                    />
                    <span className={passive ? "text-gray-400" : "text-emerald-600"}>
                      {passive ? "Pasif" : "Satışta"}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-400">Ürün bulunamadı.</div>
          )}
        </div>
      )}

      {/* Yapışkan kaydet çubuğu */}
      {dirtyList.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-charcoal/10 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <span className="text-sm font-medium text-gray-700" data-testid="quick-price-dirty-count">
              {dirtyList.length} üründe değişiklik
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={reset} disabled={saving}>
                <RotateCcw className="mr-1.5 h-4 w-4" /> Vazgeç
              </Button>
              <Button onClick={save} disabled={saving} data-testid="quick-price-save" className="bg-[#C57B43] hover:bg-[#b06d39]">
                {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}
                Değişiklikleri Kaydet ({dirtyList.length})
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
